import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const ProjectCard = ({ project, index, onViewDetails }) => {
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareText = `${project.title} - ${window.location.href}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = window.location.href;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 1200);
  };

  const slideFrom = index % 2 === 0 ? -80 : 80;

  return (
    <motion.div
      initial={{ opacity: 0, x: slideFrom, y: 40 }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      whileHover={{
        scale: 1.04,
        rotateX: 4,
        rotateY: -4,
        boxShadow:
          "0px 25px 40px rgba(0,0,0,0.25), 0 0 40px rgba(255,255,255,0.15)",
      }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative bg-surface rounded-2xl border border-border overflow-hidden cursor-pointer group"
    >
      <div className="relative h-56 overflow-hidden rounded-b-xl group">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full h-full"
        >
          <motion.div
            whileHover={{ scale: 1.12 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full h-full"
          >
            <Image
              src={project.thumbnail}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </motion.div>

        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewDetails}
            className="bg-white text-primary px-5 py-3 rounded-2xl font-semibold shadow-xl"
          >
            View Case Study
          </motion.button>
        </div>

        <motion.div
          initial={{ x: -10, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="absolute top-4 left-4"
        >
          <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md">
            {project.category}
          </span>
        </motion.div>
      </div>

      <div className="p-6 space-y-3">
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center justify-between"
        >
          <h3 className="text-lg font-bold text-text-primary group-hover:text-primary transition-all">
            {project.title}
          </h3>
          <div className="flex items-center gap-1 text-text-secondary group-hover:text-primary transition-all">
            <Icon name="Calendar" size={16} />
            <span className="text-sm">
              {new Date(project.completionDate).getFullYear()}
            </span>
          </div>
        </motion.div>

        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Icon name="Building" size={16} />
          <motion.span
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {project.client}
          </motion.span>
        </div>

        <motion.div className="flex flex-wrap gap-2">
          {project.technologies.slice(0, 3).map((tech, idx) => (
            <motion.span
              key={idx}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: idx * 0.08 }}
              className="bg-secondary-100 text-secondary-700 px-2 py-1 rounded text-xs font-medium"
            >
              {tech}
            </motion.span>
          ))}

          {project.technologies.length > 3 && (
            <motion.span
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.3 }}
              className="bg-secondary-100 text-secondary-700 px-2 py-1 rounded text-xs font-medium"
            >
              +{project.technologies.length - 3} more
            </motion.span>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 4 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-text-secondary text-sm line-clamp-2"
        >
          {project.description}
        </motion.p>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <motion.button
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewDetails}
            className="flex items-center gap-2 text-primary font-medium"
          >
            <span>View Details</span>
            <Icon name="ArrowRight" size={16} />
          </motion.button>

          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowShare(true)}
              className="p-2 text-text-secondary hover:text-primary"
            >
              <Icon name="Share2" size={16} />
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              className="p-2 text-text-secondary hover:text-primary"
            >
              <Icon name="Bookmark" size={16} />
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showShare && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowShare(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white p-6 rounded-2xl shadow-xl w-80"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 text-center">
                Share This Project
              </h3>

              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden mb-2">
                <input
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm text-gray-700 outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="px-3 py-2 bg-primary text-white text-sm font-medium"
                >
                  Copy
                </button>
              </div>

              {copied && (
                <p className="text-green-600 text-sm mb-3 text-center">
                  Link Copied!
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                >
                  <Icon name="MessageCircle" size={24} className="text-green-500" />
                  <span className="text-sm mt-1">WhatsApp</span>
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    window.location.href
                  )}`}
                  target="_blank"
                  className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                >
                  <Icon name="Facebook" size={24} className="text-blue-600" />
                  <span className="text-sm mt-1">Facebook</span>
                </a>

                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                >
                  <Icon name="Instagram" size={24} className="text-pink-500" />
                  <span className="text-sm mt-1">Instagram</span>
                </a>

                <a
                  href={`mailto:?subject=${encodeURIComponent(
                    project.title
                  )}&body=${encodeURIComponent(shareText)}`}
                  className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                >
                  <Icon name="Mail" size={24} className="text-red-500" />
                  <span className="text-sm mt-1">Email</span>
                </a>
              </div>

              <button
                onClick={() => setShowShare(false)}
                className="mt-4 w-full py-2 rounded-2xl bg-primary text-white font-medium"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProjectCard;
